import { useMemo } from 'react';
import { useDebounce } from 'ahooks';
import { useQuery } from '@tanstack/react-query';
import isEqual from 'lodash/isEqual';
import uniq from 'lodash/uniq';
import type { BlueprintRevisions } from 'types/api.types';

export const REVISION_SEARCH_DEBOUNCE_MS = 500;
const REVISION_SEARCH_STALE_MS = 5 * 60 * 1000;

type Props = {
  availableRevisions?: BlueprintRevisions | null;
  search: string;
  // Identifies the repository searched, so results are cached per repository and term
  queryKey: readonly unknown[];
  searchRevisions?: (search: string) => Promise<BlueprintRevisions | undefined>;
};

export const useRevisionSearch = ({ availableRevisions, search, queryKey, searchRevisions }: Props) => {
  const debouncedSearch = useDebounce(search.trim(), { wait: REVISION_SEARCH_DEBOUNCE_MS });
  // Each search is an agent round trip, so only search when the listed revisions are incomplete
  const enabled = Boolean(searchRevisions && availableRevisions?.truncated && debouncedSearch);

  const { data: searchedRevisions, isFetching } = useQuery({
    queryKey: [...queryKey, 'revisions-search', debouncedSearch],
    queryFn: () => searchRevisions!(debouncedSearch),
    enabled,
    // Keep the last term's matches on screen while the next term loads, but never another repository's
    placeholderData: (previousData, previousQuery) =>
      isEqual(previousQuery?.queryKey.slice(0, queryKey.length), queryKey) ? previousData : undefined,
    staleTime: REVISION_SEARCH_STALE_MS,
    retry: false
  });

  const revisions = useMemo(() => {
    if (!enabled || !searchedRevisions) return availableRevisions ?? undefined;
    return {
      heads: uniq([...(availableRevisions?.heads ?? []), ...searchedRevisions.heads]),
      tags: uniq([...(availableRevisions?.tags ?? []), ...searchedRevisions.tags])
    };
  }, [enabled, availableRevisions, searchedRevisions]);

  return { revisions, searching: enabled && isFetching };
};
